import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Home, AppWindow, Clock, MapPin, Settings, LogOut, Shield, Smartphone } from "lucide-react";

const parentNavItems = [
  { to: "/dashboard", icon: Home, label: "Home" },
  { to: "/apps", icon: AppWindow, label: "Apps" },
  { to: "/screen-time", icon: Clock, label: "Screen Time" },
  { to: "/location", icon: MapPin, label: "Location" },
  { to: "/settings", icon: Settings, label: "Settings" },
];

const adminNavItem = { to: "/devices", icon: Smartphone, label: "Devices" };

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const isAdmin = user?.is_admin || user?.role === "admin";
  const navItems = isAdmin ? [...parentNavItems.slice(0, 4), adminNavItem, parentNavItems[4]] : parentNavItems;

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#FFF8F0] font-nunito" data-testid="app-layout">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 h-full w-[240px] flex-col bg-white border-r border-[#F0E4D8]/50 z-40">
        <div className="p-6 flex items-center gap-3">
          <div className="w-10 h-10 bg-[#FF5C39] rounded-2xl flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" strokeWidth={3} />
          </div>
          <span className="text-xl font-extrabold text-[#1F1F2E] tracking-tight">Alfie</span>
        </div>

        <nav className="flex-1 px-3 py-2 space-y-1">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
              className={() =>
                `flex items-center gap-3 px-4 py-3 rounded-2xl font-bold text-sm transition-all ${
                  isActive(to)
                    ? "bg-[#FF5C39] text-white shadow-[0_8px_20px_rgba(255,92,57,0.25)]"
                    : "text-[#52526B] hover:bg-[#FFF0E0] hover:text-[#FF5C39]"
                }`
              }
            >
              <Icon className="w-5 h-5" strokeWidth={isActive(to) ? 3 : 2.5} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-[#F0E4D8]/50">
          <div className="flex items-center gap-3 px-3 mb-3">
            <div className="w-9 h-9 bg-[#FFD23F] rounded-full flex items-center justify-center text-[#1F1F2E] font-extrabold text-sm">
              {user?.name?.charAt(0)?.toUpperCase() || "P"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-[#1F1F2E] truncate">{user?.name || "Parent"}</p>
              <p className="text-xs text-[#52526B] truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="flex items-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm font-bold text-[#52526B] hover:bg-[#FFF0E0] hover:text-[#F43F5E] transition-all"
          >
            <LogOut className="w-4 h-4" strokeWidth={2.5} />
            Log out
          </button>
        </div>
      </aside>

      {/* Mobile Top Bar */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-[#F0E4D8]/50 flex items-center px-4 z-40">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#FF5C39] rounded-xl flex items-center justify-center">
            <Shield className="w-4 h-4 text-white" strokeWidth={3} />
          </div>
          <span className="text-lg font-extrabold text-[#1F1F2E]">Alfie</span>
        </div>
        <div className="flex-1" />
        <button onClick={logout} data-testid="mobile-logout-button" className="p-2 text-[#52526B]">
          <LogOut className="w-5 h-5" strokeWidth={2.5} />
        </button>
      </header>

      {/* Main Content */}
      <main className="md:ml-[240px] pt-14 md:pt-0 pb-24 md:pb-8">
        <div className="max-w-5xl mx-auto p-4 md:p-8">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#F0E4D8]/50 z-40 safe-bottom">
        <div className="flex items-center justify-around py-2">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`mobile-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
              className={() =>
                `flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all ${
                  isActive(to) ? "text-[#FF5C39]" : "text-[#52526B]"
                }`
              }
            >
              <Icon className="w-5 h-5" strokeWidth={isActive(to) ? 3 : 2} />
              <span className="text-[10px] font-bold">{label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
